import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export const WR_BORDER = ["#9D00FF", "#5B3BFF", "#00F0FF"];

export function useCountUp(value, duration = 750) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const start = from.current, end = Number(value) || 0, t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const v = Math.round(start + (end - start) * (1 - Math.pow(1 - p, 3)));
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(step);
      else from.current = end;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); from.current = end; };
  }, [value, duration]);
  return shown;
}

export function CountUp({ value, className = "", style, testId }) {
  return <span className={className} style={style} data-testid={testId}>{useCountUp(value).toLocaleString()}</span>;
}

export function GradCard({ delay = 0, className = "", testId, bg, children }) {
  return (
    <div data-testid={testId} className={`wr-card wr-rise rounded-2xl p-[2px] ${className}`}
         style={{ background: `linear-gradient(140deg, ${WR_BORDER.join(", ")})`, animationDelay: `${delay}ms`, "--wr-glow": "#5B3BFF" }}>
      <div className="relative h-full rounded-[14px] bg-[#110726] bg-cover bg-center p-4 overflow-hidden"
           style={bg ? { backgroundImage: `linear-gradient(180deg, rgba(17,7,38,0.45), rgba(17,7,38,0.72)), url('${bg}')` } : undefined}>
        <div className="wr-drift pointer-events-none absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-25 blur-3xl bg-[#9D00FF]" />
        <div className="wr-drift pointer-events-none absolute -bottom-28 -left-20 w-56 h-56 rounded-full opacity-15 blur-3xl bg-[#00F0FF]" style={{ animationDelay: "-6s" }} />
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

export function SectionHeading({ icon: Icon, color, children, className = "mb-4", testId, small, action }) {
  return (
    <div className={className} data-testid={testId}>
      <div className="flex items-center gap-2.5">
        <Icon size={small ? 20 : 24} strokeWidth={2.6} style={{ color, filter: `drop-shadow(0 0 6px ${color})` }} className="shrink-0" />
        <h2 className={`font-graffiti uppercase leading-none ${small ? "text-xl" : "text-2xl"}`} style={{ color, textShadow: `0 0 14px ${color}99, 2px 2px 0 #000` }}>
          {children}
        </h2>
        {action}
      </div>
      <div className="mt-2 h-[3px] rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${color} 25%, ${color} 70%, transparent)`, boxShadow: `0 0 10px ${color}88` }} />
    </div>
  );
}

function SplashBurst({ color }) {
  const drops = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const a = (i / 14) * Math.PI * 2 + Math.random() * 0.4;
    const d = 38 + Math.random() * 34;
    return { i, dx: Math.cos(a) * d, dy: Math.sin(a) * d * 0.75, s: 3 + Math.random() * 5, dl: Math.random() * 80 };
  }), []);
  return (
    <span className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" data-testid="stat-splash-burst" aria-hidden="true">
      <span className="absolute w-10 h-10 rounded-full" style={{ border: `3px solid ${color}`, boxShadow: `0 0 16px ${color}`, animation: "shop-ring 0.7s ease-out forwards" }} />
      {drops.map((p) => (
        <span key={p.i} className="absolute rounded-full" style={{ width: p.s, height: p.s, background: color, boxShadow: `0 0 8px ${color}`,
          animation: `shop-particle 0.8s ${p.dl}ms cubic-bezier(.2,.7,.3,1) forwards`, "--dx": `${p.dx}px`, "--dy": `${p.dy}px` }} />
      ))}
    </span>
  );
}

function Splash({ color, value, label }) {
  const shown = useCountUp(value);
  const prev = useRef(value);
  const [burst, setBurst] = useState(0);
  useEffect(() => {
    if (value > prev.current) {
      setBurst((b) => b + 1);
      const t = setTimeout(() => setBurst(0), 1100);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);
  const len = String(value).length;
  return (
    <div className="relative w-[104px] h-[54px] shrink-0 flex items-center justify-center" data-burst={burst ? "true" : "false"} data-testid={`stat-splash-${label.toLowerCase()}`}>
      <img src={`/ui/UI_Splash_Stats_${label}.webp`} alt="" key={burst || "idle"} draggable={false}
           className={`absolute inset-0 w-full h-full object-contain transition-transform duration-300 group-hover:rotate-[-5deg] group-hover:scale-110 ${burst ? "wr-wobble" : ""}`}
           style={{ filter: `drop-shadow(0 0 8px ${color}88)` }} aria-hidden="true" />
      {burst > 0 && <SplashBurst key={burst} color={color} />}
      <span className={`relative font-num leading-none text-[#0A051A] ${burst ? "wr-num-pop" : ""} ${len >= 5 ? "text-[22px]" : len === 4 ? "text-[27px]" : "text-[34px]"}`}
            style={{ textShadow: "0 1px 0 rgba(255,255,255,0.4)" }} data-testid={`stat-value-${label.toLowerCase()}`}>{shown}</span>
    </div>
  );
}

const TIP_POS = { left: "left-0", center: "left-1/2 -translate-x-1/2", right: "right-0" };

export function HintTip({ text, color = "#00F0FF", align = "left", testId, className = "", children }) {
  return (
    <span className={`relative group/hint cursor-help ${className}`} data-testid={testId}>
      {children}
      <span role="tooltip" data-testid={testId && `${testId}-tooltip`}
            className={`pointer-events-none absolute top-full mt-2 ${TIP_POS[align]} z-50 w-56 rounded-lg border bg-[#0A051A]/95 backdrop-blur-md px-3 py-2 text-left font-mono text-[11px] leading-snug normal-case tracking-normal text-[#E5DCFF] opacity-0 translate-y-1 transition-[opacity,transform] duration-150 group-hover/hint:opacity-100 group-hover/hint:translate-y-0`}
            style={{ borderColor: `${color}AA`, boxShadow: `0 0 16px ${color}44` }}>
        {text}
      </span>
    </span>
  );
}

export function StatBlock({ label, value, color, icon: Icon, hint }) {
  return (
    <div data-testid={`stat-${label.toLowerCase()}`}
         className="wr-stat group relative flex items-center gap-2 rounded-xl px-2.5 py-2 border-2 bg-[#0A051A]/80 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:z-20"
         style={{ borderColor: `${color}55`, "--wr-glow": color }}>
      <HintTip text={hint} color={color} testId={`stat-hint-${label.toLowerCase()}`} className="shrink-0 flex">
        <Icon size={40} strokeWidth={2.4} className="transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-110" style={{ color, filter: `drop-shadow(0 0 8px ${color})` }} data-testid={`stat-icon-${label.toLowerCase()}`} />
      </HintTip>
      <span className="font-graffiti text-[15px] uppercase tracking-wide text-white flex-1 min-w-0 truncate">{label}</span>
      <Splash color={color} value={value} label={label} />
    </div>
  );
}

export function ActionTile({ testId, color, icon: Icon, label, onClick, className = "" }) {
  return (
    <button data-testid={testId} onClick={onClick}
            className={`wr-action group relative ${className} flex flex-col items-center justify-center gap-1.5 rounded-xl border-[3px] px-2 py-3 overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 active:scale-95`}
            style={{ borderColor: color, background: `linear-gradient(180deg, ${color}38 0%, ${color}14 55%, #0A051A 100%)`, boxShadow: `0 0 14px ${color}55, inset 0 0 18px ${color}22`, "--wr-glow": color }}>
      <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `radial-gradient(circle at 50% 30%, ${color}55, transparent 70%)` }} />
      <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100" />
      <Icon size={40} strokeWidth={2.8} className="relative transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-6" style={{ color, filter: `drop-shadow(0 0 6px ${color})` }} />
      <span className="relative font-heading font-black text-sm uppercase tracking-widest text-white">{label}</span>
    </button>
  );
}

export function AccompItem({ icon: Icon, color, label, value, extra, testId, className = "" }) {
  return (
    <div data-testid={testId} className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 bg-[#0A051A]/80 border transition-colors duration-200 hover:bg-[#1C0D40] ${className}`} style={{ borderColor: `${color}44` }}>
      <Icon size={15} strokeWidth={2.5} className="shrink-0 transition-transform duration-200 group-hover:scale-125" style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
      <span className="text-[10px] font-mono uppercase tracking-wider text-[#C9BDF0] flex-1 min-w-0 truncate">{label}{extra}</span>
      <span className="font-num text-base leading-none tracking-wider" style={{ color, textShadow: `0 0 8px ${color}88` }}>{value}</span>
    </div>
  );
}

const CONF_COLORS = ["#FFD700", "#FFE600", "#FF007F", "#00F0FF", "#9D00FF", "#39FF14"];

export function LevelUpSpotlight({ fx, anchorRef }) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) setPos({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  }, [fx.key, anchorRef]);
  const bits = useMemo(() => Array.from({ length: 70 }, (_, i) => {
    const a = Math.random() * Math.PI * 2, d = 120 + Math.random() * 300;
    return { i, dx: Math.cos(a) * d, dy: Math.sin(a) * d * 0.8 - 140, size: 5 + Math.random() * 7, color: CONF_COLORS[i % CONF_COLORS.length],
             rot: Math.random() * 720 - 360, delay: Math.random() * 0.15, round: Math.random() < 0.4 };
  }), [fx.key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pos) return null;
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none overflow-hidden" data-testid="levelup-spotlight">
      <div className="absolute" style={{ left: pos.x, top: pos.y }}>
        {bits.map((p) => (
          <span key={p.i} className="absolute stage-unlock-conf" style={{ width: p.size, height: p.round ? p.size : p.size * 1.6, background: p.color,
            borderRadius: p.round ? "50%" : 2, boxShadow: `0 0 8px ${p.color}AA`, animationDelay: `${p.delay}s`, "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, "--rot": `${p.rot}deg` }} />
        ))}
      </div>
    </div>
  );
}
